import { CaretLeftIcon, CaretRightIcon } from "@phosphor-icons/react";
import dayjs from "apps/visits/packages/shared/libs/dayjs";
import { useState } from "react";
import { DayPicker, getDefaultClassNames } from "react-day-picker";
import { pl } from "react-day-picker/locale";
import "react-day-picker/style.css";
import { useTranslation } from "react-i18next";
import { cn } from "@/core/shared/v2/lib/utils";
import type { AvailableSlot } from "../../models";

type CalendarTimeSelectProps = {
  availableSlots: AvailableSlot[];
  value?: string | Date | null;
  onChange: (slotStartsAt: string) => void;
};

const DATE_KEY = "YYYY-MM-DD";
// Health Actions run on just 1–2 collection days, so we list the real days as tiles instead of a whole-month
// calendar (which forces the user to hunt for the few enabled days). Above this count tiles get unwieldy, so
// we fall back to the month calendar — in practice only for atypical / long-running actions.
const MAX_DAY_TILES = 10;

export const CalendarTimeSelect = ({ availableSlots, value, onChange }: CalendarTimeSelectProps) => {
  const { t } = useTranslation();
  const defaultClassNames = getDefaultClassNames();

  // API returns every slot at once; group client-side by calendar day.
  const slotsByDate = availableSlots.reduce<Record<string, AvailableSlot[]>>((acc, slot) => {
    const key = dayjs(slot.startsAt).format(DATE_KEY);
    (acc[key] ??= []).push(slot);
    return acc;
  }, {});
  const dateKeys = Object.keys(slotsByDate).sort();
  const availableDateKeys = new Set(dateKeys);

  const valueDay = value && dayjs(value).isValid() ? dayjs(value) : null;
  const valueKey = valueDay ? valueDay.format(DATE_KEY) : null;

  const [pickedKey, setPickedKey] = useState<string | null>(null);
  // Effective day whose slots are shown: explicit pick → day of the current value (edit) → first/nearest day.
  const validPicked = pickedKey && slotsByDate[pickedKey] ? pickedKey : null;
  const selectedKey = validPicked ?? valueKey ?? dateKeys[0] ?? null;
  const daySlots = selectedKey ? (slotsByDate[selectedKey] ?? []) : [];

  const isSlotSelected = (slot: AvailableSlot) => !!valueDay && valueDay.isSame(dayjs(slot.startsAt));

  const handleDaySelect = (key: string | null) => {
    setPickedKey(key);
    // Switching the day must drop a time picked on another day, otherwise the form would keep a slot that no
    // longer matches the shown date (CTA stays enabled without a visible pick).
    if (valueKey && valueKey !== key) onChange("");
  };

  if (dateKeys.length === 0) return null;

  const useTiles = dateKeys.length <= MAX_DAY_TILES;
  const singleDay = dateKeys.length === 1;

  return (
    <div className="flex flex-col gap-6">
      {singleDay ? (
        // One collection day → nothing to choose; show it and let the user pick a time.
        <p className="font-semibold text-base">
          <span className="capitalize">{dayjs(selectedKey).format("dddd")}</span>
          {`, ${dayjs(selectedKey).format("D MMMM")}`}
        </p>
      ) : (
        <div>
          <p className="mb-3 font-semibold text-muted-foreground text-sm">{`${t("TIME_SELECT.SELECT_DATE")} *`}</p>
          {useTiles ? (
            <div className="flex flex-wrap gap-2">
              {dateKeys.map((key) => {
                const isSelected = key === selectedKey;
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => handleDaySelect(key)}
                    className={cn(
                      "flex min-w-[132px] flex-col items-start rounded-lg border-2 bg-background px-4 py-3 transition-colors",
                      isSelected ? "border-dodger-blue-700" : "border-input/50 hover:border-input",
                    )}
                  >
                    <span className="font-semibold text-base capitalize">{dayjs(key).format("dddd")}</span>
                    <span className="text-muted-foreground text-sm">{dayjs(key).format("D MMMM")}</span>
                  </button>
                );
              })}
            </div>
          ) : (
            <DayPicker
              className="mx-auto w-fit"
              mode="single"
              locale={pl}
              weekStartsOn={1}
              selected={selectedKey ? dayjs(selectedKey).toDate() : undefined}
              onSelect={(date) => handleDaySelect(date ? dayjs(date).format(DATE_KEY) : null)}
              disabled={(date) => !availableDateKeys.has(dayjs(date).format(DATE_KEY))}
              defaultMonth={selectedKey ? dayjs(selectedKey).toDate() : undefined}
              components={{
                Chevron: ({ orientation }) =>
                  orientation === "left" ? <CaretLeftIcon className="size-4" /> : <CaretRightIcon className="size-4" />,
                DayButton: ({ day: _day, modifiers, className, ...props }) => (
                  <button
                    type="button"
                    {...props}
                    className={cn(
                      className,
                      modifiers.today && !modifiers.selected && "!text-dodger-blue-700",
                      modifiers.selected && "!border-2 !border-dodger-blue-700 !bg-white !text-dodger-blue-700",
                    )}
                  />
                ),
              }}
              classNames={{
                button_previous: cn(defaultClassNames.button_previous, "rounded-md hover:bg-stone-100"),
                button_next: cn(defaultClassNames.button_next, "rounded-md hover:bg-stone-100"),
                caption_label: cn(defaultClassNames.caption_label, "font-semibold text-base capitalize"),
                weekday: cn(defaultClassNames.weekday, "font-normal text-muted-foreground"),
                day_button: cn(defaultClassNames.day_button, "rounded-full transition-colors hover:bg-stone-100"),
              }}
            />
          )}
        </div>
      )}

      <div>
        <p className="mb-3 font-semibold text-muted-foreground text-sm">{`${t("TIME_SELECT.SELECT_TIME")} *`}</p>
        <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
          {daySlots.map((slot) => {
            const isSelected = isSlotSelected(slot);
            return (
              <button
                key={slot.startsAt}
                type="button"
                onClick={() => onChange(slot.startsAt)}
                className={cn(
                  "flex flex-col items-center rounded-lg border-2 bg-background px-2 py-2 font-medium text-sm transition-colors",
                  isSelected ? "border-dodger-blue-700" : "border-input/50 hover:border-primary",
                )}
              >
                <span className="font-semibold text-base">{dayjs(slot.startsAt).format("HH:mm")}</span>
                <span className="text-muted-foreground text-xs">
                  {t("B2B.SLOTS_AVAILABLE")} {slot.quantity}
                </span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};
